import styled from 'styled-components';
import Color from 'color';
import { lFColor, lFColor0, lFColor2, mobileRes } from 'pages/components/LFHeader/LFHeader.styled';
import { bgColor } from 'pages/styles';
import { leftPanelWidth } from '../Layout.styled';

const ICON_MUTED = Color(lFColor2).alpha(0.55).toString();
const RAIL_BG = '#ffffff';
const HOVER_BG = Color(lFColor0).alpha(0.06).toString();
const SELECTED_BG = Color(lFColor0).alpha(0.12).toString();
const DIVIDER = Color(lFColor2).alpha(0.2).toString();
const expandedRailWidth = '173px';
/** Inset so the glyph stays put when the rail grows — never centered in a wide bar. */
const railGlyphInset = '14px';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: fixed;
  bottom: 0;
  left: 0;
  width: ${leftPanelWidth};
  transition: width 180ms ease;
  background: ${RAIL_BG};
  background-image:
    linear-gradient(
      180deg,
      ${Color(lFColor0).alpha(0.07).toString()} 0%,
      transparent 120px
    );
  border-right: 1px solid ${DIVIDER};
  box-shadow:
    1px 0 0 ${Color(lFColor0).alpha(0.03).toString()},
    4px 0 24px ${Color(lFColor2).alpha(0.04).toString()};
  flex: 1;
  z-index: 1000;
  height: 100svh;
  overflow: hidden;

  @media (min-width: calc(${mobileRes} + 1px)) {
    &:hover {
      width: ${expandedRailWidth};
    }
  }

  @media(max-width: ${mobileRes}) {
    position: relative;
    top: 0;
    left: 0;
    width: 180px;
    box-shadow:
      4px 0 32px ${Color(lFColor2).alpha(0.12).toString()};
  }
`;

export const Cont1 = styled.div`
`;

/** Top rail brand mark — logo stays left so it does not jump when the rail grows. */
export const AppLogo = styled.button`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 56px;
  padding: 0 12px 0 ${railGlyphInset};
  flex-shrink: 0;
  box-sizing: border-box;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  overflow: hidden;
  gap: 10px;

  img {
    height: 32px !important;
    width: 32px !important;
    border-radius: 8px !important;
    filter: none !important;
    object-fit: cover;
    flex-shrink: 0;
  }

  svg {
    width: 32px;
    height: 32px;
    padding: 4px;
    box-sizing: border-box;
    flex-shrink: 0;
    border-radius: 8px;
    color: ${lFColor0};
    background: ${Color(lFColor0).alpha(0.1).toString()};
  }

  span {
    font-size: 13px !important;
    letter-spacing: -0.01em !important;
    opacity: 0;
    pointer-events: none;
    transition: opacity 140ms ease;
  }

  ${Container}:hover & span {
    opacity: 1;
    pointer-events: auto;
  }

  &:hover img,
  &:hover svg {
    opacity: 0.85;
  }

  @media(max-width: ${mobileRes}) {
    padding: 0 12px;

    span {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

export const HotelRailName = styled.span`
  font-weight: 700;
  color: ${lFColor2};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

export const ErpOpenRow = styled.div`
  padding: 4px 6px 8px;
  flex-shrink: 0;
  width: 100%;
  box-sizing: border-box;

  & > button {
    display: block;
    width: 100%;
    min-width: 0;
    padding: 0;
    border: none;
    background: transparent;
    font-family: inherit;
    cursor: pointer;
    color: inherit;
  }
`;

export const IconCont1 = styled.div<{ isSelected: boolean }>`
  padding: 8px;
  border-radius: 12px;
  position: relative;
  background: ${({ isSelected }) => (isSelected ? SELECTED_BG : 'transparent')};
  margin-top: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;
  color: ${lFColor2};
  transition: background 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
  width: 100%;
  box-sizing: border-box;

  svg {
    color: ${ICON_MUTED};
    flex-shrink: 0;
    transition: color 0.18s ease;
  }

  &:hover {
    background: ${({ isSelected }) => (!isSelected ? HOVER_BG : SELECTED_BG)};
  }
`;

export const IconText = styled.div`
  margin-left: 12px;
  font-size: 12.5px;
  font-weight: 500;
  letter-spacing: 0.01em;
  /* >1 so glyph descenders (g/y/p) aren’t clipped by the row height */
  line-height: 1.25;
  margin-right: 4px;
  color: ${Color(lFColor2).alpha(0.78).toString()};
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0;
  pointer-events: none;
  transition: opacity 140ms ease;

  ${Container}:hover & {
    opacity: 1;
    pointer-events: auto;
  }

  @media(max-width: ${mobileRes}) {
    opacity: 1;
    pointer-events: auto;
  }
`;

export const Icons = styled.div`
  padding: 10px 6px 8px;
  display: flex;
  flex-direction: column;
  gap: 17px;
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none;
  align-items: stretch;

  & > a {
    display: block;
    width: 100%;
    min-width: 0;
  }

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const Divider = styled.div`
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    ${DIVIDER} 20%,
    ${DIVIDER} 80%,
    transparent
  );
  margin: 4px 12px;
  flex-shrink: 0;
`;

export const Icon = styled(IconCont1)`
  height: 40px;
  min-height: 40px;
  padding: 0 ${railGlyphInset};
  margin-top: 0;
  margin-left: 0;
  margin-right: 0;
  width: 100%;
  overflow: hidden;
  color: ${({ isSelected }) => (isSelected ? lFColor : lFColor2)};
  background: ${({ isSelected }) => (isSelected ? SELECTED_BG : 'transparent')};
  box-shadow: none;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: ${({ isSelected }) => (isSelected ? '60%' : '0')};
    border-radius: 0 3px 3px 0;
    background: ${lFColor0};
    transition: height 0.18s ease;
  }

  svg {
    color: ${({ isSelected }) => (isSelected ? lFColor0 : ICON_MUTED)} !important;
    transition: color 0.18s ease, transform 0.18s ease;
  }

  ${IconText} {
    color: ${({ isSelected }) => (
      isSelected
        ? lFColor
        : Color(lFColor2).alpha(0.72).toString()
    )};
    font-weight: ${({ isSelected }) => (isSelected ? 600 : 500)};
  }

  &:hover {
    background: ${({ isSelected }) => (isSelected ? SELECTED_BG : HOVER_BG)};

    svg {
      color: ${lFColor0} !important;
      transform: scale(1.06);
    }

    ${IconText} {
      color: ${lFColor2};
    }
  }

  &:active {
    transform: scale(0.98);
  }
`;

export const Icons2 = styled.div`
  padding: 8px 6px 14px;
  gap: 17px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 100%;
  box-sizing: border-box;
  background: linear-gradient(
    180deg,
    transparent,
    ${Color(bgColor).alpha(0.85).toString()} 40%
  );

  & > a {
    display: block;
    width: 100%;
    min-width: 0;
  }
`;

export const Icon2 = styled(Icon)`
  border-radius: 50%;
  width: 52px;
  height: 52px;
`;
